import React, { Suspense, useEffect } from 'react'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { BrowserRouter } from 'react-router-dom'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'

import appConfig from 'app.config'
import ErrorFallbackPage from 'pages/error-fallback/ErrorFallbackPage'
import CircleSpinner from 'components/circle-spinner/CircleSpinner'
import { logError } from 'appinsights/appinsights'
import RootRoute from 'pages/route/RootRoute'

import './App.scss'
import './App.avarda.scss'

import {
	faSortDown,
	faSortUp,
	faChevronDown,
	faChevronUp,
	faDownload,
	faPhoneAlt,
	faEnvelope,
	faSignInAlt,
	faCookieBite,
	faInfoCircle,
	faTimesCircle,
	faTrashAlt,
	faSearchDollar,
	faGhost,
} from '@fortawesome/free-solid-svg-icons'
import { library } from '@fortawesome/fontawesome-svg-core'
import { UseAuthProvider } from 'contexts/AuthContext'
import { avardaBranding, shouldUseShield } from 'utils/logoHelper'
library.add(
	faChevronDown,
	faChevronUp,
	faDownload,
	faPhoneAlt,
	faEnvelope,
	faSignInAlt,
	faCookieBite,
	faInfoCircle,
	faSortUp,
	faSortDown,
	faTimesCircle,
	faTrashAlt,
	faSearchDollar,
	faGhost,
)

const MetaTags = ({ isEnvProd }: any) => {
	const description = document.querySelector('meta[name="description"]')
	if (description) {
		description.remove()
	}
	const getTitle = () => {
		switch (appConfig.country) {
			case 'de':
				return 'Bewerbungsseite | TF Bank'
			case 'dk':
				return 'My Pages | TF Bank'
			case 'ee':
				return 'TF Bank iseteenindus'
			case 'fi':
				return 'Omat sivut | TF Bank'
			case 'es':
				return 'Application Page | TF Bank Spain'
			case 'no':
				return 'TF Bank Minside'
			case 'se':
				return 'Mina Sidor | TF Bank Nordic'
			case 'uk':
				return 'Application Page | Avarda Bank UK'
			default:
				return 'Application Page | TF Bank'
		}
	}

	const getDesc = () => {
		switch (appConfig.country) {
			case 'de':
				return 'Bewerbungsseite | TF Bank'
			case 'dk':
				return 'My Pages | TF Bank'
			case 'ee':
				return 'Vaata infot oma arve, maksegraafiku või laenujäägi kohta'
			case 'fi':
				return 'Tarkista oman laina- tai säästötuotteesi tiedot TF Bankin Omilta sivuilta vuorokauden ympäri. Kirjaudu sisään!'
			case 'es':
				return 'My Pages TF Bank Spain'
			case 'no':
				return 'TF Bank Minside'
			case 'se':
				return 'Mina Sidor TF Bank Nordic'
			case 'uk':
				return 'Application Page | Avarda Bank UK'
			default:
				return 'Application Pages TF Bank'
		}
	}

	const getMeta = () => {
		if (isEnvProd) {
			return [
				{
					name: 'description',
					content: `${getDesc()}`,
				},
			]
		} else {
			return [
				{
					name: 'description',
					content: `${getDesc()}`,
				},
				{
					name: 'robots',
					content: 'none',
				},
			]
		}
	}

	return (
		<Helmet title={getTitle()} meta={getMeta()}>
			{shouldUseShield(appConfig.country) ? (
				<link rel="icon" id="favicon" href="/tf_favicon.ico" />
			) : avardaBranding(appConfig.country) ? (
				<link rel="icon" id="favicon" href="/avarda_favicon.ico" />
			) : null}
		</Helmet>
	)
}

function errorFallback({ error, resetErrorBoundary }: FallbackProps) {
	return (
		<ErrorFallbackPage
			error={error}
			resetErrorBoundary={resetErrorBoundary}
		/>
	)
}

const App: React.FC = () => {
	useEffect(() => {
		if (avardaBranding(appConfig.country)) {
			document.body.classList.add('avarda-theme')
			return () => {
				document.body.classList.remove('avarda-theme')
			}
		}
	}, [])

	useEffect(() => {
		const lang = appConfig.langAttribute
		const html = document.documentElement

		const applyLang = () => {
			if (html.lang !== lang) {
				html.lang = lang
			}
		}

		applyLang()

		const observer = new MutationObserver(() => {
			applyLang()
		})

		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['lang'],
		})

		return () => observer.disconnect()
	}, [])

	const isEnvProd = process.env.REACT_APP_ENVIRONMENT === 'production'

	return (
		<HelmetProvider>
			<BrowserRouter>
				<div className={'App ' + appConfig.country}>
					<Suspense fallback={<CircleSpinner />}>
						<ErrorBoundary
							fallbackRender={errorFallback}
							onError={(e) => logError(e)}>
							<UseAuthProvider>
								<MetaTags isEnvProd={isEnvProd} />
								<RootRoute />
							</UseAuthProvider>
						</ErrorBoundary>
					</Suspense>
				</div>
			</BrowserRouter>
		</HelmetProvider>
	)
}

export default App
